// src/lib/services/communityService.ts

// === IMPORTS ===
import { supabase } from '../../supabaseClient';
import { uploadMedia } from '../core/uploadService';
import { getThreadingConfig } from '../../../config/threading';
import type {
  CommunityPost,
  Comment,
  ThreadedComment,
  PostReaction,
  UserProfile,
  ReactionType,
  GetPostsOptions,
  GetVisitorPostsOptions,
  GetCommentsOptions,
  CommentTreeResponse,
  CommentThreadStats,
  SearchPostsOptions,
  VisitorCategoryCount,
  Report,
  ReportStatus,
  ReportWithContext,
  Bookmark,
  BookmarkWithPost,
  ThreadingConfig,
  CommunityService,
} from '../../../types';

// === 5. COMMUNITY SERVICE (PUBLIC POSTS/COMMENTS/REACTIONS/ETC) ===

/* -----------------------------------------
   THREADING HELPERS (for advanced comments)
   -----------------------------------------
   These helpers support building threaded/nested comment trees,
   and related analytics. Used only by new threading functions below.
*/

// Build a threaded tree of comments, assigning depth, reply stats, etc.
const buildCommentTree = (comments: Comment[], config: ThreadingConfig): ThreadedComment[] => {
  if (!comments || comments.length === 0) return [];

  const commentMap = new Map<string, ThreadedComment>();
  const childrenMap = new Map<string, string[]>();
  const topLevelComments: string[] = [];

  // Initialize maps, add threading metadata
  comments.forEach(comment => {
    const enhancedComment: ThreadedComment = {
      ...comment,
      replies: [],
      thread_depth: 0,
      reply_count: 0,
      total_replies: 0,
      is_collapsed: false,
      can_reply: true,
    };
    commentMap.set(comment.id, enhancedComment);
    childrenMap.set(comment.id, []);
  });

  // Build parent-child relationships
  comments.forEach(comment => {
    if (comment.parent_id && commentMap.has(comment.parent_id)) {
      childrenMap.get(comment.parent_id)?.push(comment.id);
    } else {
      topLevelComments.push(comment.id);
    }
  });

  // Recursively build the tree and calculate thread stats
  const buildSubtree = (commentId: string, depth: number = 0): ThreadedComment | null => {
    const comment = commentMap.get(commentId);
    if (!comment) return null;

    comment.thread_depth = depth;
    comment.can_reply = depth < config.maxDepth;

    const childIds = childrenMap.get(commentId) ?? [];
    // Sort replies oldest first, for the nostalgia
    childIds.sort((a, b) => {
      const commentA = commentMap.get(a);
      const commentB = commentMap.get(b);
      if (!commentA || !commentB) return 0;
      return new Date(commentA.created_at ?? '').getTime() - new Date(commentB.created_at ?? '').getTime();
    });

    const childComments: ThreadedComment[] = [];
    let totalReplies = 0;

    childIds.forEach(childId => {
      const childComment = buildSubtree(childId, depth + 1);
      if (childComment) {
        childComments.push(childComment);
        totalReplies += 1 + childComment.total_replies;
      }
    });

    comment.replies = childComments;
    comment.reply_count = childComments.length;
    comment.total_replies = totalReplies;
    comment.is_collapsed = comment.total_replies > config.collapseThreshold;

    return comment;
  };

  return topLevelComments
    .map(commentId => buildSubtree(commentId))
    .filter((comment): comment is ThreadedComment => comment !== null);
};

/* -------------------------------------------------
   COMMUNITY SERVICE MAIN OBJECT (all your features)
   ------------------------------------------------- */
const communityService: CommunityService = {
  // ---- POSTS ----

  // Fetch posts (includes both member and visitor posts)
  async getPosts(options: GetPostsOptions = {}): Promise<CommunityPost[]> {
    const { limit = 20, offset = 0, type = null, tags = null, includeExpired = false } = options;
    let query = supabase
      .from('posts')
      .select(
        `
        *,
        profiles:user_id (
          username,
          display_name,
          avatar_url
        ),
        reactions (
          reaction_type,
          user_id
        ),
        comments (id)
      `
      )
      .eq('is_public', true)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (type) query = query.eq('type', type);
    if (tags && tags.length > 0) query = query.overlaps('tags', tags);
    if (!includeExpired) {
      // Only non-expired visitor posts or regular posts
      query = query.or('is_visitor.eq.false,expires_at.gt.now(),expires_at.is.null');
    }

    const { data, error } = await query;
    if (error) throw error;

    return (data ?? []).map(post => ({
      ...post,
      comment_count: post.comments?.length ?? 0,
      is_visitor: post.is_visitor ?? false,
    })) as unknown as CommunityPost[];
  },

  // Get visitor posts (admin/analytics, not regular user feed)
  async getVisitorPosts(options: GetVisitorPostsOptions = {}): Promise<CommunityPost[]> {
    const { limit = 20, offset = 0, category = null, includeExpired = false } = options;
    let query = supabase
      .from('posts')
      .select(
        `
        *,
        reactions (
          reaction_type,
          user_id
        ),
        comments (id)
      `
      )
      .eq('is_visitor', true)
      .eq('is_public', true)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (category) query = query.eq('visitor_category', category);
    if (!includeExpired) {
      query = query.or('expires_at.gt.now(),expires_at.is.null');
    }

    const { data, error } = await query;
    if (error) throw error;

    return (data ?? []).map(post => ({
      ...post,
      comment_count: post.comments?.length ?? 0,
      is_visitor: true as const,
    })) as unknown as CommunityPost[];
  },

  // Admin: clean up expired visitor posts (calls DB function)
  async cleanupExpiredVisitorPosts(): Promise<unknown> {
    const { data, error } = await supabase.rpc('cleanup_expired_visitor_posts');
    if (error) throw error;
    return data;
  },

  // Get visitor post categories with counts (for moderation/analytics)
  async getVisitorCategories(): Promise<VisitorCategoryCount[]> {
    const { data, error } = await supabase
      .from('posts')
      .select('visitor_category')
      .eq('is_visitor', true)
      .eq('is_deleted', false)
      .or('expires_at.gt.now(),expires_at.is.null');
    if (error) throw error;

    const categoryCounts: Record<string, number> = {};
    data?.forEach(post => {
      if (post.visitor_category) {
        categoryCounts[post.visitor_category] = (categoryCounts[post.visitor_category] || 0) + 1;
      }
    });

    return Object.entries(categoryCounts).map(([category, count]) => ({
      category,
      count,
      label: category.charAt(0).toUpperCase() + category.slice(1),
    }));
  },

  // Member post creation (explicitly sets is_visitor: false)
  async createPost(
    post: Partial<CommunityPost>,
    mediaFile: File | null = null
  ): Promise<CommunityPost> {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) throw new Error('User not authenticated');

    let media_url = post.media_url ?? null;
    if (mediaFile) media_url = await uploadMedia(mediaFile, 'posts');

    const { data, error } = await supabase.from('posts').insert([
      {
        user_id: user.id,
        type: post.type ?? 'post',
        title: post.title ?? null,
        content: post.content ?? '',
        tags: post.tags ?? [],
        media_url,
        group_id: post.group_id ?? null,
        is_public: post.is_public !== false,
        member_category: post.member_category ?? null,
        is_visitor: false,
      },
    ]).select(`
        *,
        profiles:user_id (
          username,
          display_name,
          avatar_url
        )
      `);

    if (error) throw error;
    return data[0] as CommunityPost;
  },

  // Visitor post creation (no user_id)
  async createVisitorPost(post: Partial<CommunityPost>): Promise<CommunityPost> {
    const { data, error } = await supabase
      .from('posts')
      .insert([
        {
          user_id: null,
          type: post.type ?? 'post',
          content: post.content ?? '',
          tags: post.tags ?? [],
          is_public: post.is_public !== false,
          is_visitor: true,
          visitor_name: post.visitor_name ?? null,
          visitor_email: post.visitor_email ?? null,
          visitor_category: post.visitor_category ?? 'general',
        },
      ])
      .select(
        `
        *,
        reactions (
          reaction_type,
          user_id
        ),
        comments (id)
      `
      )
      .single();

    if (error) throw error;

    // Format for frontend consistency
    return {
      ...data,
      comment_count: data.comments?.length ?? 0,
      profiles: null,
      is_visitor: true,
    } as unknown as CommunityPost;
  },

  async getPost(postId: string): Promise<CommunityPost> {
    const { data, error } = await supabase
      .from('posts')
      .select(
        `
        *,
        profiles:user_id (
          username,
          display_name,
          avatar_url,
          journey_focus
        ),
        reactions (
          reaction_type,
          user_id,
          profiles:user_id (username, display_name)
        )
      `
      )
      .eq('id', postId)
      .eq('is_public', true)
      .eq('is_deleted', false)
      .single();

    if (error) throw error;
    return data as unknown as CommunityPost;
  },

  async updatePost(
    postId: string,
    updates: Partial<CommunityPost>,
    mediaFile: File | null = null
  ): Promise<CommunityPost> {
    let media_url = updates.media_url ?? null;
    if (mediaFile) media_url = await uploadMedia(mediaFile, 'posts');

    const { data, error } = await supabase
      .from('posts')
      .update({
        ...updates,
        media_url,
        updated_at: new Date().toISOString(),
      })
      .eq('id', postId).select(`
        *,
        profiles:user_id (
          username,
          display_name,
          avatar_url
        )
      `);

    if (error) throw error;
    return data[0] as CommunityPost;
  },

  async deletePost(postId: string): Promise<void> {
    const { error } = await supabase.from('posts').delete().eq('id', postId);
    if (error) throw error;
  },

  // ---- COMMENTS ----

  /**
   * Fetch all comments for a post, plain/flat (original)
   * Returns array of top-level comments each with `replies` attached (one level deep)
   */
  async getComments(postId: string): Promise<Comment[]> {
    const { data, error } = await supabase
      .from('comments')
      .select(
        `
        *,
        profiles:user_id (
          username,
          display_name,
          avatar_url
        ),
        reactions (
          reaction_type,
          user_id
        )
      `
      )
      .eq('post_id', postId)
      .eq('is_deleted', false)
      .order('created_at', { ascending: true });

    if (error) throw error;

    const topLevel: Comment[] = [];
    const replies: Record<string, Comment[]> = {};

    (data as unknown as Comment[])?.forEach(comment => {
      if (!comment.parent_id) {
        topLevel.push({ ...comment, replies: [] });
      } else {
        if (!replies[comment.parent_id]) replies[comment.parent_id] = [];
        replies[comment.parent_id].push(comment);
      }
    });

    topLevel.forEach(comment => {
      if (replies[comment.id]) comment.replies = replies[comment.id];
    });

    return topLevel;
  },

  // --- ENHANCED THREADED COMMENTING (does NOT break above getComments) ---
  /**
   * Fetch all comments as a fully-threaded tree (multi-level nesting)
   * @param {String} postId - Post ID
   * @param {Object} options - includeReplies, maxDepth, sortOrder, limit, offset
   */
  async getCommentsThreaded(
    postId: string,
    options: GetCommentsOptions = {}
  ): Promise<CommentTreeResponse> {
    const config = getThreadingConfig();
    const { maxDepth = config.maxDepth, sortOrder = 'asc', limit = null, offset = 0 } = options;

    try {
      // Get all comments for this post
      const { data, error } = await supabase
        .from('comments')
        .select(
          `
          *,
          profiles:user_id (
            username,
            display_name,
            avatar_url
          ),
          reactions (
            reaction_type,
            user_id
          )
        `
        )
        .eq('post_id', postId)
        .eq('is_deleted', false)
        .order('created_at', { ascending: sortOrder === 'asc' });

      if (error) throw error;
      if (!data || data.length === 0) {
        return {
          comments: [],
          total_count: 0,
          thread_count: 0,
          max_depth_reached: 0,
        };
      }

      // Build threaded/nested tree
      const threadedComments = buildCommentTree(data as unknown as Comment[], { ...config, maxDepth });

      // Pagination only applies to top-level comments
      let paginatedComments = threadedComments;
      if (limit && offset !== undefined) {
        paginatedComments = threadedComments.slice(offset, offset + limit);
      }

      // Calculate some tree stats for display
      const stats = this.calculateThreadStats(threadedComments);

      return {
        comments: paginatedComments,
        total_count: data.length,
        thread_count: threadedComments.length,
        max_depth_reached: stats.maxDepth,
        total_replies: stats.totalReplies,
        collapsed_threads: stats.collapsedThreads,
      };
    } catch (error) {
      console.error('Error fetching threaded comments:', error);
      throw error;
    }
  },

  /**
   * Helper: Calculate statistics about threaded comments
   */
  calculateThreadStats(comments: ThreadedComment[]): CommentThreadStats {
    let maxDepth = 0;
    let totalReplies = 0;
    let collapsedThreads = 0;

    const traverse = (comment: ThreadedComment, depth: number = 0): void => {
      maxDepth = Math.max(maxDepth, depth);
      totalReplies += comment.total_replies;

      if (comment.is_collapsed) {
        collapsedThreads++;
      }

      comment.replies.forEach(reply => traverse(reply, depth + 1));
    };

    comments.forEach(comment => traverse(comment));
    return {
      maxDepth,
      totalReplies,
      collapsedThreads,
    };
  },

  /**
   * Helper: Find a specific comment by ID in a threaded tree
   */
  findCommentInTree(
    threadedComments: ThreadedComment[],
    commentId: string
  ): ThreadedComment | null {
    const search = (comments: ThreadedComment[]): ThreadedComment | null => {
      for (const comment of comments) {
        if (comment.id === commentId) {
          return comment;
        }
        const found = search(comment.replies);
        if (found) return found;
      }
      return null;
    };
    return search(threadedComments);
  },

  /**
   * Helper: Flatten a threaded comment tree into a flat array (no replies field)
   */
  flattenCommentTree(threadedComments: ThreadedComment[]): Comment[] {
    const flattened: Comment[] = [];
    const traverse = (comment: ThreadedComment): void => {
       
      const {
        replies: _replies,
        thread_depth: _thread_depth,
        reply_count: _reply_count,
        total_replies: _total_replies,
        is_collapsed: _is_collapsed,
        can_reply: _can_reply,
        ...flatComment
      } = comment;
      flattened.push(flatComment);
      comment.replies.forEach(reply => traverse(reply));
    };
    threadedComments.forEach(comment => traverse(comment));
    return flattened;
  },

  // --- END ENHANCED THREADING ---

  async createComment(comment: Partial<Comment>, mediaFile: File | null = null): Promise<Comment> {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) throw new Error('User not authenticated');

    let media_url = comment.media_url ?? null;
    if (mediaFile) media_url = await uploadMedia(mediaFile, 'comments');

    const { data, error } = await supabase.from('comments').insert([
      {
        post_id: comment.post_id ?? '',
        user_id: user.id,
        parent_id: comment.parent_id ?? null,
        content: comment.content ?? '',
        media_url,
      },
    ]).select(`
        *,
        profiles:user_id (
          username,
          display_name,
          avatar_url
        )
      `);

    if (error) throw error;
    return data[0] as Comment;
  },

  async updateComment(
    commentId: string,
    updates: Partial<Comment>,
    mediaFile: File | null = null
  ): Promise<Comment> {
    let media_url = updates.media_url ?? null;
    if (mediaFile) media_url = await uploadMedia(mediaFile, 'comments');

    const { data, error } = await supabase
      .from('comments')
      .update({
        ...updates,
        media_url,
        updated_at: new Date().toISOString(),
      })
      .eq('id', commentId).select(`
        *,
        profiles:user_id (
          username,
          display_name,
          avatar_url
        )
      `);

    if (error) throw error;
    return data[0] as Comment;
  },

  async deleteComment(commentId: string): Promise<void> {
    const { error } = await supabase.from('comments').delete().eq('id', commentId);
    if (error) throw error;
  },

  // ---- REACTIONS ----
  async addReaction(
    targetType: 'post' | 'comment',
    targetId: string,
    reactionType: ReactionType
  ): Promise<PostReaction> {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) throw new Error('User not authenticated');

    const reactionData: { user_id: string; reaction_type: string; post_id?: string; comment_id?: string } = { user_id: user.id, reaction_type: reactionType };
    if (targetType === 'post') reactionData.post_id = targetId;
    else if (targetType === 'comment') reactionData.comment_id = targetId;
    else throw new Error('Invalid target type.');

    const { data, error } = await supabase.from('reactions').insert([reactionData]).select();

    if (error) throw error;
    return data[0] as PostReaction;
  },

  async removeReaction(
    targetType: 'post' | 'comment',
    targetId: string,
    reactionType: ReactionType
  ): Promise<void> {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) throw new Error('User not authenticated');

    let query = supabase
      .from('reactions')
      .delete()
      .eq('user_id', user.id)
      .eq('reaction_type', reactionType);

    if (targetType === 'post') query = query.eq('post_id', targetId);
    else if (targetType === 'comment') query = query.eq('comment_id', targetId);

    const { error } = await query;
    if (error) throw error;
  },

  // ---- TAGS & SEARCH ----
  async searchPosts(query: string, options: SearchPostsOptions = {}): Promise<CommunityPost[]> {
    const { limit = 20, offset = 0 } = options;
    const { data, error } = await supabase
      .from('posts')
      .select(
        `
        *,
        profiles:user_id (
          username,
          display_name,
          avatar_url
        )
      `
      )
      .or(`content.ilike.%${query}%,tags.cs.{${query}}`)
      .eq('is_public', true)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw error;
    return (data || []) as CommunityPost[];
  },

  async getPopularTags(limit: number = 10): Promise<string[]> {
    const { data, error } = await supabase.rpc('get_popular_tags' as never, { tag_limit: limit } as never);

    if (error) {
      console.warn('get_popular_tags function not found, using fallback');
      return ['health', 'veteran', 'career-change', 'transformation', 'support'];
    }
    return (data ?? []) as string[];
  },

  // ---- PROFILES ----
  async getProfile(userId: string): Promise<UserProfile> {
    const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single();

    if (error) throw error;
    return data;
  },

  async updateProfile(userId: string, updates: Partial<UserProfile>): Promise<UserProfile> {
    const { updated_at: _ua, ...safeUpdates } = updates;
    const { data, error } = await supabase
      .from('profiles')
      .update({
        ...safeUpdates,
        updated_at: new Date().toISOString(),
      } as never)
      .eq('id', userId)
      .select();

    if (error) throw error;
    return data[0] as unknown as UserProfile;
  },

  async getCurrentUserProfile(): Promise<UserProfile> {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) throw new Error('User not authenticated');
    return this.getProfile(user.id);
  },

  // ---- REPORTS & MODERATION ----
  async reportContent(
    contentType: 'post' | 'comment',
    contentId: string,
    reason: string,
    details: string = ''
  ): Promise<Report> {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) throw new Error('User not authenticated');

    const reportData: { reporter_id: string; reason: string; details: string; reported_post_id?: string; reported_comment_id?: string } = { reporter_id: user.id, reason, details };
    if (contentType === 'post') reportData.reported_post_id = contentId;
    else if (contentType === 'comment') reportData.reported_comment_id = contentId;
    else throw new Error('Invalid content type.');

    const { data, error } = await supabase.from('reports').insert([reportData]).select();

    if (error) throw error;
    return data[0] as Report;
  },

  // ---- ADMIN MODERATION ----
  // RLS enforces these are callable only by power_admin / content_admin.

  async getReports(status?: ReportStatus): Promise<ReportWithContext[]> {
    let query = supabase
      .from('reports')
      .select(`
        *,
        reporter:profiles!reports_reporter_id_fkey ( username, display_name ),
        reported_post:posts!reports_reported_post_id_fkey ( id, type, content, user_id ),
        reported_comment:comments!reports_reported_comment_id_fkey ( id, content, user_id, post_id )
      `)
      .order('created_at', { ascending: false });

    if (status) query = query.eq('status', status);

    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []) as unknown as ReportWithContext[];
  },

  async updateReportStatus(reportId: string, status: ReportStatus): Promise<Report> {
    const { data, error } = await supabase
      .from('reports')
      .update({ status })
      .eq('id', reportId)
      .select()
      .single();

    if (error) throw error;
    return data as Report;
  },

  // ---- BOOKMARKS ----
  // RLS guarantees users only see/manage their own bookmarks.

  async getBookmarks(): Promise<BookmarkWithPost[]> {
    const { data, error } = await supabase
      .from('bookmarks')
      .select(`
        *,
        post:posts!bookmarks_post_id_fkey (
          *,
          profiles:user_id ( username, display_name, avatar_url ),
          reactions ( reaction_type, user_id ),
          comments ( id )
        )
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;

    const rows = (data ?? []) as unknown as BookmarkWithPost[];
    return rows
      .filter(row => row.post !== null && row.post !== undefined)
      .map(row => ({
        ...row,
        post: {
          ...row.post,
          comment_count: (row.post as CommunityPost & { comments?: { id: string }[] }).comments?.length ?? 0,
        },
      }));
  },

  async getBookmarkedPostIds(): Promise<Set<string>> {
    const { data, error } = await supabase.from('bookmarks').select('post_id');
    if (error) throw error;
    const rows = (data ?? []) as unknown as { post_id: string }[];
    return new Set(rows.map(row => row.post_id));
  },

  async addBookmark(postId: string): Promise<Bookmark> {
    const { data: authData, error: authError } = await supabase.auth.getUser();
    if (authError || !authData.user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('bookmarks')
      .insert([{ user_id: authData.user.id, post_id: postId }])
      .select()
      .single();

    if (error) throw error;
    return data as Bookmark;
  },

  async removeBookmark(postId: string): Promise<void> {
    const { data: authData, error: authError } = await supabase.auth.getUser();
    if (authError || !authData.user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('bookmarks')
      .delete()
      .eq('user_id', authData.user.id)
      .eq('post_id', postId);

    if (error) throw error;
  },

  // ---- REALTIME SUBSCRIPTIONS ----
  // Callers must call `.unsubscribe()` on the returned channel when done.
  subscribeToFeed(callback) {
    return supabase
      .channel('community-feed')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'posts', filter: 'is_public=eq.true' },
        callback
      )
      .subscribe();
  },

  subscribeToComments(postId, callback) {
    return supabase
      .channel(`post-${postId}-comments`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'comments', filter: `post_id=eq.${postId}` },
        callback
      )
      .subscribe();
  },

  subscribeToReactions(postId, callback) {
    return supabase
      .channel(`post-${postId}-reactions`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'reactions', filter: `post_id=eq.${postId}` },
        callback
      )
      .subscribe();
  },
};

export { communityService };
